import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Loader2, CheckCircle2, AlertTriangle, Truck } from "lucide-react";

export default function RaiseLabelCap() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [state, setState] = useState("loading"); // loading | ok | error
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const done = useRef(false);

  useEffect(() => {
    if (done.current) return;
    done.current = true;
    if (!token) { setState("error"); setError("This link is missing its token."); return; }
    api.post("/label-cap/redeem", { token })
      .then(r => { setResult(r.data); setState("ok"); })
      .catch(e => { setError(formatApiError(e.response?.data?.detail) || "This link is invalid or has already been used."); setState("error"); });
  }, [token]);

  return (
    <div className="max-w-lg mx-auto px-6 py-20" data-testid="raise-label-cap-page">
      <div className="card-flat p-8 text-center">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[var(--ff-muted)] mb-4">
          <Truck size={22} className="text-[var(--ff-primary)]"/>
        </div>
        {state === "loading" && (
          <div data-testid="raise-cap-loading">
            <Loader2 size={28} className="animate-spin mx-auto text-[var(--ff-primary)]"/>
            <p className="mt-4 text-sm text-[var(--ff-text-muted)]">Raising your shipping-label limit…</p>
          </div>
        )}
        {state === "ok" && result && (
          <div data-testid="raise-cap-success">
            <CheckCircle2 size={34} className="mx-auto text-emerald-600"/>
            <h1 className="font-display text-3xl mt-3">Limit raised</h1>
            <p className="mt-2 text-[var(--ff-text-secondary)]">
              We added <strong>${Number(result.bump).toFixed(0)}</strong> to today's shipping-label limit for{" "}
              <strong>{result.farm_name}</strong>.
            </p>
            <p className="mt-4 inline-block rounded-lg bg-[var(--ff-muted)] px-4 py-3 text-sm" data-testid="raise-cap-newcap">
              New limit today: <strong className="text-[var(--ff-primary)]">${Number(result.new_cap).toFixed(2)}</strong>
              <span className="text-[var(--ff-text-muted)]"> · spent ${Number(result.spent_today).toFixed(2)}</span>
            </p>
            <p className="mt-4 text-xs text-[var(--ff-text-muted)]">Resets at midnight UTC. You can buy labels again now.</p>
            <Link to="/vendor" className="btn-primary mt-6 inline-flex" data-testid="raise-cap-dashboard">Back to your dashboard</Link>
          </div>
        )}
        {state === "error" && (
          <div data-testid="raise-cap-error">
            <AlertTriangle size={34} className="mx-auto text-amber-600"/>
            <h1 className="font-display text-3xl mt-3">Couldn't raise the limit</h1>
            <p className="mt-2 text-[var(--ff-text-secondary)]">{error}</p>
            <p className="mt-4 text-xs text-[var(--ff-text-muted)]">Each link works once and expires at midnight UTC. Reply to the alert email and we'll raise it for you.</p>
            <Link to="/vendor" className="btn-secondary mt-6 inline-flex" data-testid="raise-cap-dashboard-err">Back to your dashboard</Link>
          </div>
        )}
      </div>
    </div>
  );
}
